import { createElement } from 'react';
import { Entity } from '@backstage/catalog-model';
import { createFrontendPlugin } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import {
  stackRemoteAccess,
  stackSupports,
} from '@internal/plugin-stack-common';

const remoteAccessTab = EntityContentBlueprint.make({
  name: 'remote-access',
  params: {
    path: '/console',
    title: 'Console',
    // Only where there is a machine to open a session on. A bucket or a public
    // IP has none, and the tab could only ever report that.
    filter: entity =>
      stackSupports(entity, 'console') && Boolean(stackRemoteAccess(entity)),
    loader: () =>
      import('./components/GuacamoleConsoleContent').then(m =>
        createElement(m.GuacamoleConsoleContent),
      ),
  },
});

// Replay lives in Backstage rather than in Guacamole's own UI: the
// guacamole-auth-json extension keeps no connection history, so Guacamole has
// no history screen to replay a session from (#13018).
//
// Gated on the same capability as the console, because a recording is a
// recording of a console session: a stack that can never have one can never
// have a recording either, and the tab could only ever report an empty list.
// stackSupports fails closed, so a family nobody has classified yet is left
// without the tab rather than given an empty one.
export function showsSessionRecordings(entity: Entity): boolean {
  return (
    entity.apiVersion === 'aeven.io/v1alpha1' &&
    entity.kind === 'Stack' &&
    stackSupports(entity, 'console')
  );
}

const sessionRecordingsTab = EntityContentBlueprint.make({
  name: 'session-recordings',
  params: {
    path: '/recordings',
    title: 'Recordings',
    filter: showsSessionRecordings,
    loader: () =>
      import('./components/SessionRecordingsContent').then(m =>
        createElement(m.SessionRecordingsContent),
      ),
  },
});

export const remoteAccessPlugin = createFrontendPlugin({
  pluginId: 'remote-access',
  extensions: [remoteAccessTab, sessionRecordingsTab],
});
